import { Product, ProductTemplateType } from "@hermes/api-model-product";
import { isSimpleProduct } from "@hermes/utils-generic-helpers";

import { NODATA } from "../constants/analytics.constant";

export const formatPimCodeForAnalytics = (
    pimCode: string | undefined,
): string | undefined => pimCode?.toUpperCase().replace(/_/g, "");

/**
 * Join item with pipe for products inside a complex product
 * @param items : list of complex product
 * @param key : key of field want to retrieve and combine
 * @param separator
 * @param defaultValue
 */
export function joinSetItems(
    items: unknown[] | null | undefined,
    key: string,
    separator = "|",
    defaultValue = NODATA,
): string | undefined {
    if (!items?.length) {
        return undefined;
    }
    return items
        .map((item) => {
            if (
                item &&
                typeof item === "object" &&
                key in (item as Record<string, unknown>)
            ) {
                const value = (item as Record<string, unknown>)[key];
                return value === undefined || value === ""
                    ? defaultValue
                    : String(value);
            }
            return defaultValue;
        })
        .filter((v) => v !== undefined)
        .join(separator);
}

/**
 * The ID to identify a simple product is simply the sku.
 * The ID to identify a complex product should be either :
 * - The skuSet if any defined (ex: Beltkit, Apple Watch).
 * - The main sku in any other case (ex: Bikini, Bundle, IDO).
 * @param product
 */
export const getAnalyticsProductId = (product: Product) => {
    if (isSimpleProduct(product.sku)) {
        return product.sku;
    }
    return product.attributes &&
        "skuSet" in product.attributes &&
        product.attributes.skuSet
        ? product.attributes.skuSet
        : product.sku;
};

/**
 * Extract the ID when only main sku is available.
 * @param product
 */
export const getAnalyticsProductIdFromSku = (product: { sku: string }) => {
    if (isSimpleProduct(product.sku)) {
        return product.sku;
    }
    // We only keep the sku set part if any.
    return product.sku.replace(/-.*/, "");
};

/**
 * dimension10 refers to the list of items in given complex product.
 * It should return all skus describing the product concatenated with "|".
 * By default, we extract skus from product attributes items.
 * @param product
 */
export const getAnalyticsProductDimension10 = (product: Product) => {
    if (isSimpleProduct(product.sku)) {
        return NODATA;
    }
    switch (product.templateType) {
        case ProductTemplateType.AppleWatch: {
            return `${product.appleWatchAttributes?.skuGuizmo ?? NODATA}|${product.appleWatchAttributes?.skuStrap ?? NODATA}`;
        }
        case ProductTemplateType.DoubleFragrance: {
            return `${product.doubleFragranceAttributes?.skuPerfume ?? NODATA}|${product.doubleFragranceAttributes?.skuAccessory ?? NODATA}`;
        }
        case ProductTemplateType.Beltkit: {
            return `${product.beltkitAttributes?.skuLeather ?? NODATA}|${product.beltkitAttributes?.skuBuckle ?? NODATA}`;
        }
        default: {
            return (
                (product.attributes &&
                "items" in product.attributes &&
                product.attributes.items
                    ? joinSetItems(product.attributes.items, "sku")
                    : undefined) ?? NODATA
            );
        }
    }
};
